import React from 'react';
import { connect } from 'react-redux';
import { getFormValues, change as onChange } from 'redux-form';

import isEqual from 'lodash/isEqual';
import pullAllBy from 'lodash/pullAllBy';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import get from 'lodash/get';

import { translate as t, ChipField } from 'admin-on-rest';
import {
  FlatButton,
  Table,
  TableHeader,
  TableRow,
  TableHeaderColumn,
  TableBody,
  TableRowColumn,
  CardTitle,
  Card,
  CardText,
} from 'material-ui';
import AutoComplete from 'material-ui/AutoComplete/AutoComplete';
import Trashcan from 'material-ui/svg-icons/action/delete';

import './styles.css';
import { getTranslationByLocale } from '../utils/TranslationUtils';

class IngredientListBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      descriptionSearchText: '',
      supplierOrderIdSearchText: '',
      allergens: [],
      contents: '',
    };
  }

  componentWillReceiveProps(nextProps) {
    if (
      (this.state.allergens.length === 0 &&
        nextProps.allergens.length > 0 &&
        nextProps.record.ingredientList.length > 0) ||
      !isEqual(this.props.allergens, nextProps.allergens) ||
      !isEqual(this.props.record.ingredientList, nextProps.record.ingredientList)
    ) {
      this.setState({
        allergens: this.reduceAllergens(nextProps.record.ingredientList, nextProps.allergens),
        contents: this.concatContents(nextProps.record.ingredientList),
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.record, nextProps.record) ||
      !isEqual(this.props.choices, nextProps.choices) ||
      !isEqual(this.props.allergens, nextProps.allergens) ||
      !isEqual(this.state.allergens, nextState.allergens) ||
      this.state.descriptionSearchText !== nextState.descriptionSearchText ||
      this.state.supplierOrderIdSearchText !== nextState.supplierOrderIdSearchText
    );
  }

  searchByDescription = (descriptionSearchText) => this.setState({ descriptionSearchText });
  searchBySupplierOrderId = (supplierOrderIdSearchText) => this.setState({ supplierOrderIdSearchText });

  reduceAllergens = (ingredients, allergens) =>
    ingredients
      .reduce((sum, val) => uniq([...(val.allergens || []), ...sum]), [])
      .map((allergen) => allergens.find((a) => a.name === allergen));

  concatContents = (ingredients) =>
    ingredients
      .map(
        (ingredient) =>
          `<strong>${ingredient.description}:</strong> ${ingredient.contents || this.props.translate('missingContents')}`
      )
      .join('\n');

  addIngredient = (index, element) => {
    const {
      record,
      change,
      choices: { ingredients },
    } = this.props;

    // Focus the search field again
    element.refs.searchTextField.input.focus();

    // Add the new ingredient to the list
    const newIngredientList = [...record.ingredientList, ingredients[index]];
    // Build a distinct array of all ingredient allergens
    const allergens = this.reduceAllergens(newIngredientList, this.props.allergens);
    // Build a string of all ingredient contents
    const contents = this.concatContents(newIngredientList);
    // Clear the previous query and add the allergens to state
    this.setState({ descriptionSearchText: '', supplierOrderIdSearchText: '', allergens, contents });

    return change('record-form', 'ingredientList', newIngredientList);
  };
  removeIngredient = (ingredient) => {
    const newIngredientList = this.props.record.ingredientList.filter(
      (r) => r._id !== ingredient._id
    );
    this.setState({
      allergens: this.reduceAllergens(newIngredientList, this.props.allergens),
      contents: this.concatContents(newIngredientList),
    });
    return this.props.change('record-form', 'ingredientList', newIngredientList);
  };

  hydrateDataSource = () => {
    const {
      choices: { ingredients },
      record: { ingredientList },
    } = this.props;
    if (ingredientList && ingredientList.length > 0) {
      return pullAllBy(ingredients, ingredientList, 'id');
    }
    return ingredients;
  };

  render() {
    const { record, translate } = this.props;
    const { descriptionSearchText, supplierOrderIdSearchText, allergens, contents } = this.state;

    return (
      <Card className="ingredient-list-builder">
        <CardTitle subtitle={translate('resources.products.fields.ingredientList')} />
        <CardText style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <AutoComplete
            ref={(ac) => {
              this.autocompleteDescription = ac;
            }}
            fullWidth
            floatingLabelText={`${translate('add')} ${translate('resources.ingredients.name', {
              smart_count: 1,
            })} ${translate('by')} ${translate('resources.ingredients.fields.description')}`}
            filter={AutoComplete.caseInsensitiveFilter}
            animated={false}
            searchText={descriptionSearchText}
            onNewRequest={(_, index) => this.addIngredient(index, this.autocompleteDescription)}
            onUpdateInput={this.searchByDescription}
            dataSource={this.hydrateDataSource()}
            dataSourceConfig={{ text: 'description', value: 'id' }}
          />
          <AutoComplete
            style={{ marginLeft: '1em' }}
            ref={(ac) => {
              this.autocompleteSupplierOrderId = ac;
            }}
            fullWidth
            floatingLabelText={`${translate('add')} ${translate('resources.ingredients.name', {
              smart_count: 1,
            })} ${translate('by')} ${translate('resources.ingredients.fields.supplierOrderId')}`}
            filter={AutoComplete.caseInsensitiveFilter}
            animated={false}
            searchText={supplierOrderIdSearchText}
            onNewRequest={(_, index) => this.addIngredient(index, this.autocompleteSupplierOrderId)}
            onUpdateInput={this.searchBySupplierOrderId}
            dataSource={this.hydrateDataSource()}
            dataSourceConfig={{ text: 'supplierOrderId', value: 'id' }}
          />
        </CardText>
        {record && record.ingredientList && record.ingredientList.length > 0 && (
          <Table selectable={false}>
            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
              <TableRow>
                <TableHeaderColumn>
                  {translate('resources.products.fields.contents.description')}
                </TableHeaderColumn>
                <TableHeaderColumn>
                  {translate('resources.products.fields.contents.supplier')}
                </TableHeaderColumn>
                <TableHeaderColumn>
                  {translate('resources.products.fields.contents.supplierOrderId')}
                </TableHeaderColumn>
                <TableHeaderColumn />
              </TableRow>
            </TableHeader>
            <TableBody showRowHover displayRowCheckbox={false}>
              {record &&
                record.ingredientList &&
                record.ingredientList.map((ingredient) => (
                  <TableRow key={ingredient.id}>
                    <TableRowColumn>{ingredient.description}</TableRowColumn>
                    <TableRowColumn>{ingredient.supplier}</TableRowColumn>
                    <TableRowColumn>{ingredient.supplierOrderId}</TableRowColumn>
                    <TableRowColumn>
                      <FlatButton
                        primary
                        icon={<Trashcan />}
                        label={translate('remove')}
                        onClick={() => this.removeIngredient(ingredient)}
                      />
                    </TableRowColumn>
                  </TableRow>
                ))}
              {allergens.length > 0 && (
                <TableRow>
                  <TableRowColumn colSpan="4" style={{ textAlign: 'center' }}>
                    <p>
                      <strong>{translate('resources.products.fields.allergens')}:&nbsp;</strong>
                    </p>
                    <p style={{ display: 'flex', flexFlow: 'row wrap', justifyContent: 'center' }}>
                      {allergens.map((allergen) => (
                        <ChipField
                          record={{
                            title: getTranslationByLocale(allergen.title, this.props.locale),
                          }}
                          source="title"
                        />
                      ))}
                    </p>
                  </TableRowColumn>
                </TableRow>
              )}
              {contents.length > 0 && (
                <TableRow>
                  <TableRowColumn colSpan="4" style={{ textAlign: 'left' }}>
                    <p>
                      <strong>{translate('resources.products.fields.contents')}:&nbsp;</strong>
                    </p>
                    <p
                      style={{
                        display: 'flex',
                        flexFlow: 'row wrap',
                        justifyContent: 'flex-start',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: contents }} />
                    </p>
                  </TableRowColumn>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    );
  }
}

const selector = getFormValues('record-form');

export default connect(
  (state) => ({
    record: selector(state),
    allergens: values(get(state, 'admin.resources.allergens.data', [])),
  }),
  { change: onChange }
)(t(IngredientListBuilder));
